import { SelectItem } from "./Step1SelectItem";
import { Heading, Spinner } from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { SelectParticipants } from "./Step2SelectParticipants";
import { VerifySignups } from "./Step3Verify";
import { useAwaitResponse } from "../../../Api/HttpRequestHelpers";
import {
  CompetitionClient,
  DisciplineClient,
  SignupClient,
} from "../../../Api/client/CompetitionClient";
import { Discipline_GET } from "../../../Api/dto/Discipline_GET";
import { SignupRequest } from "../../../Api/dto/SignupRequest_POST";
import PaymentComponent from "./Step5StartPayment/Step5StartPayment";
import { Role, useSessionState } from "../../../Session/SessionService";

export type SignupUnit = SignupRequest[];

export function SignupFlow(props: { competitionTag?: string }) {
  const params = useParams() as { competitionTag: string };
  const session = useSessionState();

  const asAdmin =
    props.competitionTag != undefined && session.hasAtLeastRole(Role.ADMIN);

  const { competitionTag } = {
    competitionTag: props.competitionTag ?? params.competitionTag,
  };

  const [competition, isLoadingCompetition] = useAwaitResponse(
    null,
    () => CompetitionClient.getCompetition(competitionTag),
    (response) => response.body ?? null
  );

  const [disciplines, isLoadingDisciplines] = useAwaitResponse(
    [] as Discipline_GET[],
    () => DisciplineClient.getDisciplines(competitionTag),
    (response) => response.body ?? ([] as Discipline_GET[])
  );

  const [startlist, isLoadingStartlist] = useAwaitResponse(
    null,
    () => SignupClient.getStartlist(competitionTag),
    (response) => response.body ?? null
  );

  const [occupiedTeamNames, setOccupiedTeamNames] = useState<Set<string>>(
    new Set()
  );

  const [step, setStep] = useState<number>(1);

  const [currentUnit, setCurrentUnit] = useState<SignupUnit>([]);

  const [completedUnits, setCompletedUnits] = useState<SignupUnit[]>([]);

  useEffect(() => {
    if (startlist != null) {
      const occupiedTeamNamesList = startlist.rows
        .filter((s) => !!s.groupName)
        .map((s) => s.groupName);
      setOccupiedTeamNames(new Set(occupiedTeamNamesList));
    }
  }, [isLoadingStartlist, startlist]);

  if (isLoadingCompetition) {
    return <>Laddar...</>;
  }

  const openFakedTime = {
    signupOpenTime: new Date().getTime() - 1000000,
    signupClosedTime: null,
  };

  return (
    <>
      <Heading size="lg" mb={4}>
        Anmälan till {competition?.name}
      </Heading>
      {isLoadingDisciplines ? (
        <Spinner>Laddar...</Spinner>
      ) : step === 1 ? (
        <SelectItem
          startState={
            (currentUnit.length && currentUnit[0].disciplineId) || null
          }
          onNext={(disciplineId) => {
            setCurrentUnit((old) =>
              buildCurrentUnit(
                old,
                disciplineId,
                disciplines.filter((d) => d.id == disciplineId).shift()
                  ?.groupSize ?? 1
              )
            );
            setStep((prev) => prev + 1);
          }}
          onParseSignupsFromFile={(signups: Array<SignupUnit>) => {
            setCompletedUnits((old) => [...old, ...signups]);
            setStep(3);
          }}
          disciplines={disciplines.map((d) => {
            if (asAdmin && (d.groupSize == null || d.groupSize <= 1)) {
              return { ...d, ...openFakedTime };
            }
            return d;
          })}
          competitionStartTime={new Date(competition?.startTime || new Date())}
        />
      ) : step === 2 ? (
        <SelectParticipants
          startState={currentUnit}
          onNext={(updatedUnit: SignupUnit) => {
            if (updatedUnit[0].groupName) {
              setOccupiedTeamNames((old) => {
                const newSet = new Set(old);
                newSet.add(updatedUnit[0].groupName ?? "");
                return newSet;
              });
            }
            setCompletedUnits((old) => [...old, updatedUnit]);
            setCurrentUnit([]);
            setStep((prev) => prev + 1);
          }}
          onPrev={() => setStep((prev) => prev - 1)}
          occupiedTeamNames={occupiedTeamNames}
          discipline={disciplines
            .filter((d) => d.id == currentUnit[0].disciplineId)
            .shift()}
          competition={competition}
        />
      ) : step === 3 ? (
        <VerifySignups
          startState={completedUnits}
          disciplines={disciplines}
          onNew={() => {
            setCurrentUnit([]);
            setStep(1);
          }}
          onEdit={(signupUnit: SignupUnit, completed: SignupUnit[]) => {
            setOccupiedTeamNames((old) => {
              const n = new Set(old);
              n.delete(signupUnit[0].groupName ?? "x");
              return n;
            });
            setCompletedUnits(completed);
            setCurrentUnit(signupUnit);
            setStep(1);
          }}
          onDelete={(removedUnit: SignupUnit, remainingUnits: SignupUnit[]) => {
            setOccupiedTeamNames((old) => {
              const n = new Set(old);
              n.delete(removedUnit[0].groupName ?? "x");
              return n;
            });
            setCompletedUnits(remainingUnits);
          }}
          onNext={() => {
            setStep((prev) => prev + 1);
          }}
        />
      ) : step === 4 ? (
        <PaymentComponent
          competition={competition!}
          disciplines={disciplines}
          signupUnits={completedUnits}
          onPrev={() => setStep((prev) => prev - 1)}
        />
      ) : null}
    </>
  );
}

export const VACANT = "VAKANT";

function buildCurrentUnit(
  initialState: SignupUnit,
  disciplineID: number,
  size: number
) {
  let init = initialState.map((sr) => ({ ...sr, disciplineId: disciplineID }));

  let newSignupUnit = [] as SignupUnit;
  for (let i = 0; i < size; i++) {
    newSignupUnit = [
      ...newSignupUnit,
      init.shift() ??
        ({
          firstname: size > 1 ? VACANT : undefined,
          lastname: size > 1 ? VACANT : undefined,
          disciplineId: disciplineID,
        } as SignupRequest),
    ];
  }
  return newSignupUnit;
}

/*// 
        <Payment
          signups={completedUnits}
          competition={competition}
          disciplines={disciplines}
          onSendSignupRequest={(
            responsePromise: Promise<AxiosResponse<api.SignupResponse, any>>,
            email: string,
            swishNumber: string,
            paymentMethod: PaymentMethod
          ) => {
            setSignupResponse(responsePromise);
            setPaymentMethod(paymentMethod);
            setSwishNumber(swishNumber);
            setEmail(email);
            setStep(6);
          }}
        />
      (
        <PostPaymentInfo
          onBackToPayment={() => setStep(4)}
          email={email ?? "din e-postadress "}
          signupResponsePromise={signupResponse!}
          paymentMethod={paymentMethod}
        />
      )
      */
